<template>
    <div>
        <ModuleTitleNav :next-module="nextModule" :previous-module="previousModule" class="title-one">
            {{ moduleTitle }}
        </ModuleTitleNav>
        <div class="application-module">
            <b-form-row>
                <b-col>
                    <p class="info-text">
                        After reviewing each section of the Local Application, add a comment about your
                        decision to accept, or reject this submission. Rejected Local Applications, will
                        be returned to the applicant for correction and resubmission.
                    </p>
                </b-col>
            </b-form-row>
            <b-form-row class="review-section">
                <b-col>
                    <div class="field-labels">
                        Reviewer Comments
                    </div>
                    <b-form-textarea
                        v-model="actorComment"
                        :disabled="!isReviewable"
                        :rows="5"
                        xplaceholder="Comments are required to enable 'Accept' and 'Reject' buttons below"
                    />
                </b-col>
            </b-form-row>

            <b-form-row v-if="!isReviewable" class="review-section">
                <span class="review-section-header">Review Status:</span>
                <b-col>
                    <b-badge v-if="hasBeenAccepted" variant="success" style="font-size: 1.1em;">
                        <fa-icon :icon="['fal', 'thumbs-up']" />
                        {{ acceptButtonLabel }}
                    </b-badge>

                    <b-badge v-else-if="hasBeenRejected" variant="danger" style="font-size: 1.1em;">
                        <fa-icon :icon="['fal', 'thumbs-down']" />
                        {{ rejectButtonLabel }}
                    </b-badge>

                    <b-badge v-else variant="info" style="font-size: 1.1em;">
                        Awaiting Review
                    </b-badge>
                </b-col>
            </b-form-row>

            <b-form-row v-else class="review-section">
                <b-col col-md="5" />
                <b-col xcols="3">
                    <b-button
                        id="popover-accept-button"
                        variant="outline-success"
                        :loading="isAccepting"
                        :disabled="hasNoComment"
                        @click.stop="showPopover('popover-accept-button')"
                    >
                        <fa-icon :icon="['fal', 'thumbs-up']" />
                        {{ acceptButtonLabel }}
                    </b-button>
                </b-col>
                <b-col xcols="3">
                    <b-button
                        id="popover-reject-button"
                        variant="outline-danger"
                        :loading="isRejecting"
                        :disabled="hasNoComment"
                        @click.stop="showPopover('popover-reject-button')"
                    >
                        <fa-icon :icon="['fal', 'thumbs-down']" />
                        {{ rejectButtonLabel }}
                    </b-button>
                </b-col>
                <b-col col-md="5" />
            </b-form-row>

            <!-- Accept LocalApplication Pop -->
            <b-popover :target="'popover-accept-button'" placement="top-center">
                <template slot="title" class="title-class">
                    <strong>Accept Local Application</strong>
                </template>
                <template slot="default">
                    <b-form-row>
                        <b-col>
                            <p>
                                Click the
                                <strong>Accept</strong>
                                button, if you approve of the contents of this Local Application and wish to send
                                it to the next reviewer.
                            </p>
                        </b-col>
                    </b-form-row>
                </template>
                <b-form-row>
                    <b-col ref="acceptButton" cols="6">
                        <b-button
                            size="sm"
                            block
                            class="cancel-button"
                            @click="closePopover('popover-accept-button')"
                        >
                            <strong>Cancel</strong>
                        </b-button>
                    </b-col>
                    <b-col cols="6">
                        <b-button size="sm"
                                  block
                                  variant="outline-success"
                                  @click="accept"
                        >
                            <strong>ACCEPT</strong>
                        </b-button>
                    </b-col>
                </b-form-row>
            </b-popover>

            <!-- Reject LocalApplication Pop -->
            <b-popover :target="'popover-reject-button'" placement="top-center">
                <template slot="title">
                    <strong>Reject Local Application</strong>
                </template>
                <template slot="default">
                    <b-form-row>
                        <b-col>
                            <p>
                                Click the <strong>Reject</strong> button, if you found problems with the
                                contents of this Local Application and wish to send it back to the applicant.
                            </p>
                        </b-col>
                    </b-form-row>
                </template>
                <b-form-row>
                    <b-col ref="rejectButton" cols="6">
                        <b-button
                            size="sm"
                            block
                            class="cancel-button"
                            @click="closePopover('popover-reject-button')"
                        >
                            <strong>Cancel</strong>
                        </b-button>
                    </b-col>
                    <b-col cols="6">
                        <b-button size="sm"
                                  block
                                  variant="outline-danger"
                                  @click="reject"
                        >
                            <strong>REJECT</strong>
                        </b-button>
                    </b-col>
                </b-form-row>
            </b-popover>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { Log } from '$Shared/@arc/vue-arc';

export default {
    name: 'LocalApplicationReview',

    data() {
        return {
            moduleTitle: 'Local Application Review',
            // nextModule: { name: 'localApplication.Clusters' },
            previousModule: { name: 'localApplication.History' },
            actionTaken: null,
            isAccepting: false,
            isRejecting: false,
            actorComment: null,
        };
    },

    computed: {
        ...mapState('localApplication', {
            previousActionTaken: (state) => state.localApplicationData.state.actionTaken,
            localApplicationAcademicYear: (state) => state.localApplicationData.academicYear,
            localApplicationId: 'localApplicationId',
        }),

        ...mapGetters({
            isReviewable: 'localApplication/isReviewable',
            isReviewer: 'auth/isReviewer',
            isPA: 'auth/isPA',
            isPR: 'auth/isPR',
        }),

        hasNoComment() {
            return ['', null].includes(this.actorComment);
        },

        hasBeenRejected() {
            return this.previousActionTaken === 'reject' && !this.isReviewable;
        },

        hasBeenAccepted() {
            return this.previousActionTaken === 'accept' && !this.isReviewable;
        },

        acceptButtonLabel() {
            if (this.hasBeenAccepted) {
                return 'Accepted';
            }
            return this.isPA ? 'Approve' : 'Accept';
        },

        rejectButtonLabel() {
            return this.hasBeenRejected ? 'Rejected' : 'Reject';
        },

        nextModule() {
            let nextModule = null;
            if (this.tabsAyCheck) {
                nextModule = { name: 'localApplication.Clusters' };
            } else {
                nextModule = { name: 'localApplication.Section134Instructions' };
            }
            return nextModule;
        },

        tabsAyCheck() {
            let show = false;
            if (this.localApplicationAcademicYear) {
                let Ay = Number(this.localApplicationAcademicYear);
                if (Ay >= 22) {
                    show = true;
                }
            }
            return show;
        },
    },

    methods: {
        ...mapActions('localApplication', {
            approveLocalApplication: 'approve',
            acceptLocalApplication: 'accept',
            rejectLocalApplication: 'reject',
        }),

        async accept() {
            this.actionTaken = 'accept';
            this.isAccepting = true;

            const response = await this.acceptLocalApplication({
                localApplicationId: this.localApplicationId,
                actorComment: this.actorComment,
            });

            this.isAccepting = false;
            Log.debug(response, 'COMPONENT:' + this.name + ' -> ACCEPT ACTIVITY response: ');

            this.$root.$emit('bv::hide::popover');
        },

        async reject() {
            this.actionTaken = 'reject';
            this.isRejecting = true;

            const response = await this.rejectLocalApplication({
                localApplicationId: this.localApplicationId,
                actorComment: this.actorComment,
            });

            this.isRejecting = false;
            Log.debug(response, 'COMPONENT:' + this.name + ' -> REJECT ACTIVITY response: ');

            this.$root.$emit('bv::hide::popover');
        },

        showPopover(target) {
            this.closePopover(target);
            this.$root.$emit('bv::show::popover', target);
        },

        closePopover(target) {
            // just ignore the target and close all popovers
            this.$root.$emit('bv::hide::popover');
        },
    }
};
</script>


<style lang="scss">
@import '$StyleVars';

h5 {
    font-size: 25px;
    text-align: center;
    font-weight: bold;
}

.cancel-button {
    background-color: $cancel-button-color;
    color: $edit-button-color;
    border-color: transparent;

    &:hover {
        background-color: $edit-button-color;
        color: $cancel-button-color;
        border-color: $cancel-button-color;
    }
}

.review-section {
    margin: 20px 0;
}

.review-section-header {
    font-size: 1.2rem;
    font-weight: bold;
}
</style>
